import * as React from "react";
import { BaseBlock, broadcastBlockLoad } from "sb-resp-lib";
import { sportsDict, datesDict } from "./dict";
import { config, FilterBlockOptions } from "./config";
import { FilterBlock } from "./FilterBlock";
import { sportsRetrieversFactory, sportsParserFactory, datesRetrieverFactory, datesParserFactory, sportConfig, datesConfig, sportsStateFactory, datesStateFactory, getTimeZoneObservable } from "../../modes";
import { Retriever, ModelItem, IState, ITimeZoneObservable, Parser } from "../../common";

/*
    This structure allows us to use mocks during dev
    but not to include them when we do build:release
*/

let sportRetrieverMock, datesRetrieverMock;
if (process.env.NODE_ENV === "development") {
    let _sportMocks = require("../../modes/sports/mocks");
    let _dateMocks = require("../../modes/dates/mocks");
    sportRetrieverMock =  new _sportMocks.SportRetrieverMock();
    datesRetrieverMock = new _dateMocks.DatesRetrieverMock();
}

export class FilterBlockRoot extends BaseBlock {

    options!: FilterBlockOptions;
    private state!: IState<ModelItem>;
    private parser!: Parser;
    private retrievers: Retriever<ModelItem>[] = [];
    private readonly timeZoneObservable: ITimeZoneObservable;
    // constructor sets defaults
    constructor(
        domId: string,
        options = {
            dict: sportsDict,
            cms: config,
            navHandler: {},
            dataProvider: {},
            betSlipUtil: {},
            oddsUtil: {},
            facade: {},
            gameStatus: {},
            timeControl: {},
            panelId: "",
            storageUtils: {},
            userInfo: {}
        }
    ) {
        super(domId, options);

        // if no provided dictionary is not full fill in the missing keys with the base one
        this.options.dict = { ...(options.cms.mode === "Sport" ? sportsDict : datesDict), ...(options.dict || {}) };

        // merge default and CMS options
        this.options.cms = { ...config, ...(options.cms || {}) };

        this.timeZoneObservable = getTimeZoneObservable();
        this.timeZoneObservable.activate();
        this.initState();

        if (process.env.NODE_ENV === "development") {
            //export data store to window for debug purposes
            window["FilterBlockDATA"] = this.state;
        }
    }

    private initState() {
        this.options.cms.mode === "Sport" ?
            this.initSports(this.options.cms) :
            this.initDates(this.options.cms);
    }

    private initSports(cms: sportConfig) {
        this.retrievers = process.env.NODE_ENV === "development" ? [sportRetrieverMock] : sportsRetrieversFactory(cms, this.options.dict, this.timeZoneObservable);
        this.state = sportsStateFactory(cms);
        this.parser = sportsParserFactory(cms, this.retrievers);
    }

    private initDates(cms: datesConfig) {
        const retriever = process.env.NODE_ENV === "development" ? datesRetrieverMock : datesRetrieverFactory(cms, this.options.dict, this.timeZoneObservable);
        this.retrievers.push(retriever);
        this.state = datesStateFactory(cms);
        this.parser = datesParserFactory(cms, retriever);
    }

    activate() {
        this.activateAsync().then(() => !this.isActive && this.cleanup()).catch((reason) => this.cleanup());
        return super.activate(
            <FilterBlock state={this.state} config={this.options.cms} />
        );
    }

    private async activateAsync() {
        await this.parser.activate(this.state);
        this.state.activate();
    }

    deactivate() {
        this.cleanup();
        return super.deactivate();
    }

    private cleanup() {
        this.retrievers.forEach(r => r.deactivate());
        this.state.deactivate();
        this.parser.deactivate();
        this.state.clear();
    }

    setParams(...params: any[]) {
        this.retrievers.forEach(r => r.setParams(...params));
    }

    setBranch(itemId: string) {
        this.state.setInitialItem("" + itemId);
        this.state.selectInitial();
    }

    destroy() {
        this.timeZoneObservable.deactivate();
        return this;
    }

    onPanelEnter() {
        this.retrievers.forEach(r => r.onPanelEnter());
        return this;
    }

    onPanelLeave() {
        this.retrievers.forEach(r => r.onPanelLeave());
        return this;
    }
}

window["FilterBlock"] = FilterBlockRoot;
broadcastBlockLoad("FilterBlock");